import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Typography, App, Alert } from "antd";
import {
  getPendingNdaInvitation,
  getNdaSignStatus,
} from "../../redux/slices/EsignSlice";
import CustomButton from "../../components/CustomButton/Button";
import Loader from "../../components/CustomLoader/Loader";
import { BTN_TXT } from "../../constants/constants";
import "../Sla/Sla.scss";

const { Text, Title } = Typography;

const Nda = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { pendingNda, pendingNdaLoading, checkStatusLoading } = useSelector(
    (state) => state.esign,
  );
  const [docData, setDocData] = useState({
    documentId: null,
    docketId: null,
  });
  const { documentId, docketId } = docData;

  useEffect(() => {
    dispatch(getPendingNdaInvitation());
  }, [dispatch]);

  useEffect(() => {
    if (pendingNda?.document_id && pendingNda?.docket_id) {
      setDocData((prev) => {
        if (
          prev.documentId === pendingNda.document_id &&
          prev.docketId === pendingNda.docket_id
        ) {
          return prev;
        }
        return {
          documentId: pendingNda.document_id,
          docketId: pendingNda.docket_id,
        };
      });
    }
  }, [pendingNda?.document_id, pendingNda?.docket_id]);

  const openAgreement = useCallback(
    (link) => {
      window.open(link, "_blank", "noopener,noreferrer");
      message.success(
        "NDA opened in a new tab. Please complete the signing process.",
      );
    },
    [message],
  );

  const signNdaRequest = async () => {
    try {
      const response = await dispatch(getPendingNdaInvitation()).unwrap();

      if (response?.isSigned) {
        message.success("NDA is already signed.");
        navigate("/questionnaire");
        return;
      }

      if (!response?.invitation_link) {
        message.warning(
          response?.isExpired
            ? "Your NDA signing link has expired. Please contact support or ask admin to resend."
            : "NDA has not been sent yet. Please wait for the admin to send the NDA.",
        );
        return;
      }

      setDocData({
        documentId: response.document_id,
        docketId: response.docket_id,
      });
      openAgreement(response.invitation_link);
    } catch {
      message.error("Failed to load NDA signing link.");
    }
  };

  const checkNdaStatus = useCallback(async () => {
    if (!documentId || !docketId) {
      return;
    }

    try {
      const { response } = await dispatch(
        getNdaSignStatus({ document_id: documentId, docket_id: docketId }),
      ).unwrap();

      if (response?.status === "signed") {
        message.success("NDA signed successfully!");
        dispatch(getPendingNdaInvitation());
        navigate("/questionnaire");
      } else if (response?.status === "unsigned") {
        message.warning(
          "NDA not signed yet. Please complete the signing process.",
        );
      }
    } catch {
      message.error("Failed to check NDA status.");
    }
  }, [dispatch, documentId, docketId, message, navigate]);

  useEffect(() => {
    const handleTabFocus = () => {
      if (document.visibilityState === "visible" && documentId && docketId) {
        dispatch(
          getNdaSignStatus({ document_id: documentId, docket_id: docketId }),
        )
          .unwrap()
          .then((result) => {
            if (result?.response?.status === "signed") {
              message.success("NDA signed successfully!");
              dispatch(getPendingNdaInvitation());
              navigate("/questionnaire");
            }
          })
          .catch(() => {
            // ignore focus-check errors
          });
      }
    };

    document.addEventListener("visibilitychange", handleTabFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleTabFocus);
    };
  }, [dispatch, documentId, docketId, message, navigate]);

  if (pendingNdaLoading && !pendingNda) {
    return <Loader />;
  }

  if (pendingNda?.isSigned) {
    return (
      <div className="sla-container">
        <div className="sla-content">
          <Title level={4}>Non-Disclosure Agreement (NDA)</Title>
          <Text type="secondary">Your NDA has already been signed.</Text>
          <CustomButton
            type="primary"
            style={{ marginTop: 16 }}
            onClick={() => navigate("/questionnaire")}
          >
            Continue
          </CustomButton>
        </div>
      </div>
    );
  }

  return (
    <div className="sla-container">
      <Alert
        message={
          <div>
            If your browser is blocking the signing page from opening in a new
            tab,
            <br />
            please allow pop-ups for this website and try again.
          </div>
        }
        type="info"
        showIcon
        className="info-alert"
      />

      {(checkStatusLoading || pendingNdaLoading) && <Loader />}

      <div className="sla-content">
        <Title level={4} style={{ marginBottom: 10 }}>
          Non-Disclosure Agreement (NDA)
        </Title>
        <Text type="secondary">
          {pendingNda?.isSent
            ? "Sign the NDA from here after login. You no longer need to open the email link."
            : "Waiting for admin to send the NDA. Once sent, you can sign it here without using email."}
        </Text>
        <CustomButton
          loading={pendingNdaLoading}
          type="primary"
          style={{ marginTop: 16 }}
          onClick={signNdaRequest}
          disabled={!pendingNda?.isSent && !pendingNdaLoading}
        >
          {BTN_TXT.SIGN_NDA}
        </CustomButton>
        {documentId && docketId && (
          <CustomButton
            style={{ marginTop: 12 }}
            loading={checkStatusLoading}
            onClick={checkNdaStatus}
          >
            Check Signing Status
          </CustomButton>
        )}
        <CustomButton
          style={{ marginTop: 16 }}
          type="link"
          onClick={() => navigate("/questionnaire")}
        >
          Maybe Later
        </CustomButton>
      </div>
    </div>
  );
};

export default Nda;
